import type { Viewer3DList, Viewer3DListItem, Viewer3DVariant } from "../types"

/**
 * The publish/unpublish endpoints return the DETAIL record, which carries
 * `variants` but neither `enabledVariantCount` nor `demoExternalId` — those are
 * list-only projections. Patching the cached row straight from the response
 * therefore has to re-derive them, or the row renders "On (0 of 5)" and its
 * "View demo" goes dead until the next full catalog load.
 *
 * Mirrors the server projection (`resolveDemoExternalId` / `enabledVariantCount`
 * in `viewer-3d/domain/product-3d.ts`) deliberately and in one place, so the two
 * copies stay comparable.
 */
export const deriveViewer3DListFields = (
  variants: readonly Viewer3DVariant[]
): Pick<Viewer3DListItem, "demoExternalId" | "enabledVariantCount" | "variantCount"> => {
  const demoVariant = [...variants]
    .sort((a, b) => a.order - b.order)
    .find(
      (variant) =>
        variant.threeDButtonEnabled &&
        Boolean(variant.glbUrl || variant.glbKey || variant.draftAvailable) &&
        Boolean(variant.externalId?.trim())
    )

  return {
    demoExternalId: demoVariant?.externalId?.trim() ?? null,
    enabledVariantCount: variants.filter((variant) => variant.threeDButtonEnabled).length,
    variantCount: variants.length,
  }
}

/**
 * Replaces a single row in a cached catalog page, leaving every other page and
 * the row order untouched. `previous` is optional because React Query invokes a
 * `setQueriesData` updater for every prefix match — including the placeholder
 * entry a disabled query registers, whose data is `undefined`.
 */
export const patchViewer3DListRow = (
  previous: Viewer3DList | undefined,
  productId: string,
  patch: (row: Viewer3DListItem) => Partial<Viewer3DListItem>
): Viewer3DList | undefined => {
  if (!previous) return previous
  if (!previous.items.some((item) => item.productId === productId)) return previous

  return {
    ...previous,
    items: previous.items.map((item) =>
      item.productId === productId ? { ...item, ...patch(item) } : item
    ),
  }
}
